import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Spinner } from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import { getImpuestosSucursal, descargarImpuestosExcel } from "./api"
import { rangoDelMes, descargarCSV } from "./helpers"
import HeaderFiltros from "./HeaderFiltros"
import DrawerDetalleSucursal from "./DrawerDetalleSucursal"
import ResumenTab from "./tabs/ResumenTab"
import FamiliasTab from "./tabs/FamiliasTab"
import SucursalesTab from "./tabs/SucursalesTab"
import DiarioTab from "./tabs/DiarioTab"
import ComparadorTab from "./tabs/ComparadorTab"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Impuestos", link: "#" },
  { title: "Análisis de Impuestos Generales", link: "#" },
]

const TABS = [
  { id: "resumen",     label: "Resumen",     icon: "mdi-view-dashboard-outline" },
  { id: "familias",    label: "Familias",    icon: "mdi-tag-multiple-outline" },
  { id: "sucursales",  label: "Sucursales",  icon: "mdi-source-branch" },
  { id: "comparador",  label: "Comparador",  icon: "mdi-compare-horizontal" },
  { id: "diario",      label: "Diario",      icon: "mdi-calendar-clock" },
]

const ahora = new Date()

const ImpuestosGenerales = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  const [modo, setModo] = useState("mes")
  const [year, setYear] = useState(ahora.getFullYear())
  const [month, setMonth] = useState(ahora.getMonth() + 1)
  const [fechaInicio, setFechaInicio] = useState("")
  const [fechaFin, setFechaFin] = useState("")
  const [campoFecha, setCampoFecha] = useState("fecha")
  const [sucursal, setSucursal] = useState("")
  const [incluirComparativo, setIncluirComparativo] = useState(false)

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [tabActivo, setTabActivo] = useState("resumen")

  const [filtrosAplicados, setFiltrosAplicados] = useState(null)
  const [sucursalesDisponibles, setSucursalesDisponibles] = useState([])

  const [drawerAbierto, setDrawerAbierto] = useState(false)
  const [sucursalActiva, setSucursalActiva] = useState(null)

  const [exportandoExcel, setExportandoExcel] = useState(false)
  const [exportInfo, setExportInfo] = useState(null)

  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = "Análisis de Impuestos Generales | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Análisis de Impuestos Generales"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  // Reset al cambiar empresa
  useEffect(() => {
    setData(null)
    setError("")
    setFiltrosAplicados(null)
    setSucursal("")
    setSucursalesDisponibles([])
    setTabActivo("resumen")
    setDrawerAbierto(false)
  }, [nitemisor])

  const construirFiltros = useCallback(() => {
    const base = {
      nitemisor,
      campo_fecha: campoFecha,
      sucursal: sucursal || undefined,
      incluir_comparativo: incluirComparativo,
    }
    if (modo === "mes") {
      const { fecha_inicio, fecha_fin } = rangoDelMes(year, month)
      return { ...base, fecha_inicio, fecha_fin }
    }
    return { ...base, fecha_inicio: fechaInicio, fecha_fin: fechaFin }
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin, campoFecha, sucursal, incluirComparativo])

  const puedeConsultar = useMemo(() => {
    if (!nitemisor) return false
    if (modo === "mes") return Boolean(year && month)
    return Boolean(fechaInicio && fechaFin && fechaInicio <= fechaFin)
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin])

  const cargar = useCallback(async (filtros) => {
    setLoading(true)
    setError("")
    try {
      const r = await getImpuestosSucursal(filtros)
      const body = r.data
      if (body?.success === false) throw new Error(body.error || body.message || "Error en el servidor")
      setData(body)
      // Sucursales disponibles para el filtro server-side
      const lista = Array.from(new Set((body.sucursales || []).map(s => s.sucursal))).sort()
      setSucursalesDisponibles(lista)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el análisis.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleConsultar = () => {
    const opts = construirFiltros()
    setFiltrosAplicados(opts)
    cargar(opts)
  }

  const handleAbrirDetalle = (s) => {
    setSucursalActiva(s)
    setDrawerAbierto(true)
  }

  // Export client-side: agregado de impuestos por familia + tabla principal
  const handleExportCSVResumen = () => {
    if (!data) return
    const headers = ["Sección", "Métrica", "Valor"]
    const rows = []
    const tot = data.totales_general || {}
    rows.push(["Totales", "Líneas", tot.total_lineas])
    rows.push(["Totales", "Base gravable", tot.total_base_gravable])
    rows.push(["Totales", "Impuestos", tot.total_impuestos])
    rows.push(["Totales", "Total con impuestos", tot.total_con_impuestos])
    rows.push(["Totales", "Excluidos", tot.total_excluidos ?? tot.total_exentos ?? 0])
    rows.push(["Totales", "% Gravado", tot.porcentaje_gravado])
    rows.push(["Totales", "% Excluido", tot.porcentaje_excluido ?? tot.porcentaje_exento ?? 0]);
    (data.por_familia || []).forEach(f => {
      rows.push([`Familia ${f.familia}`, "Base", f.base_gravable])
      rows.push([`Familia ${f.familia}`, "Impuesto", f.valor_impuesto])
      rows.push([`Familia ${f.familia}`, "Participación %", f.participacion_pct])
    })
    descargarCSV(`impuestos_resumen_${nitemisor}_${new Date().toISOString().split("T")[0]}.csv`, headers, rows)
  }

  // Excel ejecutivo del back (9 hojas: Portada, Por Sucursal, Detalle, Por Día,
  // Detalle Diario, Por Familia, Top, Tendencia, Auditoría).
  const handleExportarExcel = async () => {
    if (!filtrosAplicados || exportandoExcel) return
    setExportandoExcel(true)
    setExportInfo(null)
    setError("")
    try {
      const info = await descargarImpuestosExcel(filtrosAplicados)
      setExportInfo({
        filename: info.filename,
        totalSucursales: info.totalSucursales,
        totalDias: info.totalDias,
        cache: info.cache,
      })
      setTimeout(() => setExportInfo(null), 8000)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Error al exportar el Excel.")
    } finally {
      setExportandoExcel(false)
    }
  }

  if (!nitemisor) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", border: "1px dashed #cbd5e1" }}>
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> en la parte superior derecha.
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Impuestos
            </div>
            <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
              Análisis de Impuestos Generales
            </h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              <i className="mdi mdi-domain me-1 text-primary" />
              <strong>{empresaNombre}</strong> · NIT {nitemisor}
              {data?.nombre_emisor && (
                <span style={{ marginLeft: 8, color: "#94a3b8" }}>· {data.nombre_emisor}</span>
              )}
            </div>
          </div>
          {data && (
            <div className="btn-group btn-group-sm" role="group">
              <button type="button" className="btn btn-success"
                onClick={handleExportarExcel} disabled={exportandoExcel}
                title="Excel ejecutivo (9 hojas) generado por el back"
              >
                {exportandoExcel ? (
                  <><Spinner size="sm" className="me-1" /> Generando Excel…</>
                ) : (
                  <><i className="mdi mdi-microsoft-excel me-1" /> Exportar Excel</>
                )}
              </button>
              <button type="button" className="btn btn-outline-success"
                onClick={handleExportCSVResumen} title="Resumen plano en CSV">
                <i className="mdi mdi-file-delimited me-1" />
                CSV resumen
              </button>
            </div>
          )}
        </div>

        {exportInfo && (
          <div style={{
            marginTop: 12,
            background: "#d1fae5", border: "1px solid #a7f3d0",
            color: "#065f46", padding: "10px 14px", borderRadius: 8,
            fontSize: 13, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          }}>
            <i className="mdi mdi-check-circle" style={{ fontSize: 18 }} />
            <span>
              Excel descargado{exportInfo.filename ? `: ${exportInfo.filename}` : ""}
              {exportInfo.totalSucursales != null && ` · ${exportInfo.totalSucursales} sucursales`}
              {exportInfo.totalDias != null && ` · ${exportInfo.totalDias} días`}
              {exportInfo.cache === "HIT" && (
                <span style={{
                  marginLeft: 6, padding: "1px 8px", borderRadius: 999,
                  background: "#fef3c7", color: "#92400e",
                  fontSize: 11, fontWeight: 700,
                }}>
                  <i className="mdi mdi-flash me-1" />Desde caché
                </span>
              )}
            </span>
            <button type="button" className="btn-close ms-auto" style={{ fontSize: 10 }}
              onClick={() => setExportInfo(null)} />
          </div>
        )}

        <HeaderFiltros
          modo={modo} setModo={setModo}
          year={year} setYear={setYear} month={month} setMonth={setMonth}
          fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
          fechaFin={fechaFin} setFechaFin={setFechaFin}
          campoFecha={campoFecha} setCampoFecha={setCampoFecha}
          sucursal={sucursal} setSucursal={setSucursal}
          sucursalesDisponibles={sucursalesDisponibles}
          incluirComparativo={incluirComparativo} setIncluirComparativo={setIncluirComparativo}
          loading={loading}
          onConsultar={handleConsultar}
          puedeConsultar={puedeConsultar}
        />
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca",
          color: "#991b1b", borderRadius: 10,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger ms-auto"
            onClick={() => filtrosAplicados && cargar(filtrosAplicados)}>
            Reintentar
          </button>
        </div>
      )}

      {/* Loading inicial */}
      {!data && loading && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" />
          <div className="mt-2">Calculando impuestos…</div>
        </div>
      )}

      {/* Empty state */}
      {!data && !loading && !error && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "60px 40px", textAlign: "center", border: "1px dashed #cbd5e1" }}>
          <i className="mdi mdi-cash-register" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
            Configura el período y consulta
          </h5>
          <p style={{ color: "#64748b", maxWidth: 460, margin: "8px auto 0", fontSize: 14 }}>
            Indica mes/año o rango de fechas, opcionalmente filtrá por sucursal y
            activá <strong>comparativo</strong> para ver el crecimiento vs. el período anterior.
          </p>
        </div>
      )}

      {/* Tabs + contenido */}
      {filtrosAplicados && data && (
        <>
          <div style={{
            background: "#fff", borderRadius: "14px 14px 0 0",
            border: "1px solid #e2e8f0", borderBottom: "none",
            padding: "0 12px",
            display: "flex", gap: 4, overflowX: "auto",
          }}>
            {TABS.map(tab => {
              const activo = tabActivo === tab.id
              return (
                <button key={tab.id} type="button" onClick={() => setTabActivo(tab.id)} style={{
                  background: "none", border: "none", padding: "14px 18px",
                  fontSize: 13, fontWeight: 600,
                  color: activo ? "#0f172a" : "#64748b",
                  borderBottom: activo ? "3px solid #3b82f6" : "3px solid transparent",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                }}>
                  <i className={`mdi ${tab.icon}`} />
                  {tab.label}
                </button>
              )
            })}
          </div>

          <div style={{
            background: "#fff", borderRadius: "0 0 14px 14px",
            border: "1px solid #e2e8f0", borderTop: "none", padding: 16,
          }}>
            {tabActivo === "resumen"    && <ResumenTab data={data} />}
            {tabActivo === "familias"   && <FamiliasTab data={data} />}
            {tabActivo === "sucursales" && <SucursalesTab data={data} onAbrirDetalle={handleAbrirDetalle} />}
            {tabActivo === "comparador" && <ComparadorTab data={data} />}
            {tabActivo === "diario"     && <DiarioTab filtros={filtrosAplicados} />}
          </div>
        </>
      )}

      {/* Drawer detalle sucursal */}
      <DrawerDetalleSucursal
        abierto={drawerAbierto}
        sucursal={sucursalActiva}
        onCerrar={() => setDrawerAbierto(false)}
      />
    </div>
  )
}

ImpuestosGenerales.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(ImpuestosGenerales)
